<template>
  <v-container fluid>
    <div v-if="servicesOptedSorted.length > 0">
      <div v-for="(service, index) in servicesOptedSorted" :key="service.id">
        <v-form :ref="'form' + index" v-model="form[index]" @submit.prevent="submit" :class="{ 'form-disabled': isFormDisabled, 'billing-status': isBillingStatus }">
          <div class="headline">{{ service.service_name }}</div>
          <div
            v-if="!service.metadata || !service.metadata.arrange_by_owner"
            class="subtitle-2 text--darken-2 pt-2 pb-4"
          >
            Supplier : <b>{{ service.supplier }}</b>
          </div>

          <v-row>
            <!-- Hint Message -->
            <v-col
              cols="12"
              v-if="!service.metadata || !service.metadata.arrange_by_owner"
            >
              <div
                v-if="getHintMessage(service.service_name)"
                class="px-3 py-2 text-caption grey--text text--darken-2"
                style="background-color: #f9f9f9; 
                       border-left: 4px solid #bdbdbd; 
                       border-radius: 4px; 
                       margin-bottom: 8px; 
                       line-height: 1.4;
                       display: inline-block;"
              >
                {{ getHintMessage(service.service_name) }}
              </div>
            </v-col>

            <!-- Amount -->
            <v-col
              v-if="$root.hasAccess('can_manage_operation_amount_details') && (!service.metadata || !service.metadata.arrange_by_owner)"
              cols="12"
              sm="6"
              md="4"
              lg="4"
              class="pb-0"
            >
              <v-text-field
                label="Invoice Amount"
                placeholder="Enter Invoice Amount"
                outlined
                dense
                type="number"
                :rules="[rules.required]"
                v-model="service.amount"
                prepend-inner-icon="mdi-cash"
                clearable
                class="amount-field"
              ></v-text-field>
            </v-col>

            <!-- Remarks -->
            <v-col cols="12">
              <v-textarea
                label="Remarks"
                placeholder="Enter Remarks"
                outlined
                dense
                hide-details
                :rows="2"
                v-model="service.remarks"
              ></v-textarea>
            </v-col>

            <!-- Accounts Remarks -->
            <v-col cols="12" class="pb-0" v-if="$root.onlyAccountsCanAccess">
              <v-textarea
                label="Accounts Remarks"
                placeholder="Enter Accounts Remarks"
                outlined
                dense
                background-color="light-blue lighten-5"
                hide-details
                :rows="2"
                v-model="service.accounts_remarks"
                class="accounts-remarks-field"
              ></v-textarea>
            </v-col>

            <!-- Uploaded files list -->
            <v-col cols="12" class="pb-0 file-chips-field" v-if="service.file_urls">
              <v-chip-group active-class="primary--text" column>
                <v-chip
                  v-for="(file, idx) in service.file_urls"
                  outlined
                  color="primary"
                  @click="openFile(file.url)"
                  :key="idx"
                  close
                  :loading="removeLoading"
                  :disabled="removeLoading"
                  @click:close="removeFile(service, file.file_id)"
                >
                  <v-icon class="mr-2">mdi-cloud-download</v-icon>
                  {{ file.filename }}
                </v-chip>
              </v-chip-group>
            </v-col>

            <!-- File Upload -->
            <v-col
              cols="12"
              sm="6"
              md="4"
              lg="4"
              class="pb-0 file-upload-field"
              v-if="!service.metadata || !service.metadata.arrange_by_owner"
            >
              <div
                class="custom-dropzone"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop($event, service)"
                :class="{ 'drag-over': isDragging }"
              >
                <v-file-input
                  outlined
                  dense
                  chips
                  multiple
                  accept="application/pdf,image/*"
                  :loading="uploadLoading"
                  :disabled="uploadLoading"
                  v-model="filesUpload[service.id]"
                  label="Select Supporting Documents For Upload"
                  show-size
                  truncate-length="20"
                  :rules="[() => fileRequiredRule(service)]"
                  @change="uploadAsset($event, service)"
                  prepend-icon="mdi-cloud-upload"
                ></v-file-input>
              </div>
            </v-col>
          </v-row>

          <!-- Save Button -->
          <v-row>
            <v-col class="d-flex justify-end">
              <v-btn
                color="success"
                :disabled="loading[index]"
                :loading="loading[index]"
                @click="formSubmit(index)"
                outlined
              >
                Save
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-5"></v-divider>
        </v-form>
      </div>

      <!-- Multi-Select Documents Section -->
      <v-card v-if="hasMultipleDocuments" class="mt-6" elevation="2">
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-file-multiple</v-icon>
          Select Documents for Consolidation
        </v-card-title>
        <v-card-text>
          <!-- Include Vessel Report Switch -->
          <v-row class="mb-4">
            <v-col cols="12">
              <v-switch
                v-model="includeVesselReport"
                label="Include Vessel Report in Consolidated PDF"
                color="primary"
                hide-details
                inset
              ></v-switch>
            </v-col>
          </v-row>
          
          <v-divider class="mb-4"></v-divider>
          
          <v-checkbox
            v-for="document in allDocuments"
            :key="document.id"
            v-model="selectedDocuments"
            :value="document.id"
            :label="`${document.service_name} - ${document.filename}`"
            color="primary"
            hide-details
            class="mb-2"
          ></v-checkbox>
          
          <v-row v-if="selectedDocuments.length > 0" class="mt-4">
            <v-col cols="12">
              <v-btn
                color="success"
                large
                :loading="consolidatePdfLoading"
                :disabled="consolidatePdfLoading"
                @click="consolidateSelectedDocuments"
              >
                <v-icon left>mdi-file-pdf-box</v-icon>
                Consolidate PDF ({{ selectedDocuments.length }} documents)
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Report Details -->
      <ReportDetails
        :operation="operation"
        :showConsolidatedPdf="true"
        :servicesOptedSorted="servicesOptedSorted"
      />

      <!-- Submit For Approval Section -->
      <v-card v-if="['Open'].includes(operation.status) || (operation.status === 'Review_Requested' && ['manager', 'admin'].includes($store.state.auth.profile.role))" class="mt-6" elevation="2">
        <v-card-text class="text-center">
          <v-row align="center">
            <v-col cols="auto display-1">
              Submit For Approval
            </v-col>
            <v-col cols="auto">
              <v-select
                v-model="selectedManager"
                :items="managers"
                label="Select Manager"
                outlined
                dense
                hide-details
                item-text="name"
                item-value="id"
              ></v-select>
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" :disabled="!selectedManager" :loading="submitForApprovalLoading" large @click="submitForApproval">Submit</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Approval requested status section -->
      <v-card v-if="['Review_Requested'].includes(operation.status)" class="mt-6" elevation="2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" sm="6" md="4" lg="4">
              <v-chip color="primary">Approval Requested</v-chip>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="4">
              <div class="text-h6 mb-3 text-left">
                <v-icon color="primary">mdi-account</v-icon> {{ operation.manager_name }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="4">
              <div class="text-h6 mb-3 text-left">
                <v-icon color="primary">mdi-calendar</v-icon> {{ formatApprovalDate(operation.review_requested_at) }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Approved status section -->
      <v-card v-if="['Billing', 'Completed'].includes(operation.status)" class="mt-6" elevation="2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" sm="6" md="4" lg="4">
              <v-chip color="success">Approved successfully</v-chip>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="4">
              <div class="text-h6 mb-3 text-left">
                <v-icon>mdi-account</v-icon> {{ operation.approved_by_name }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="4">
              <div class="text-h6 mb-3 text-left">
                <v-icon>mdi-calendar</v-icon> {{ formatApprovalDate(operation.approved_time) }}
              </div>
            </v-col>
            <v-col cols="12">
              <div class="text-h6 mb-3 text-left">
                <span class="font-weight-bold">Remarks:</span> {{ operation.remarks_approval }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Release Port Call Section (Only for Accounts/Admin when status is Billing) -->
      <v-card v-if="operation.status === 'Billing' && ['accounts', 'admin'].includes($store.state.auth.profile.role)" class="mt-6" elevation="2">
        <v-card-text class="text-center">
          <p class="mb-4 text-body-2 grey--text">
            Release this port call back to manager review state.
          </p>
          <v-btn
            color="warning"
            large
            :loading="releasePortCallLoading"
            :disabled="releasePortCallLoading"
            @click="releasePortCall"
          >
            <v-icon left>mdi-undo</v-icon>
            Release Port Call
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Final Approval Section -->
      <v-card v-if="operation.status === 'Review_Requested' && (operation.manager_id === $store.state.auth.profile.id || $store.state.auth.profile.role === 'admin')" class="mt-6" elevation="2">
        <v-card-text class="text-center">
          <div class="text-h6 mb-3">Final Approval</div>
          <v-textarea
            v-model="finalApprovalComment"
            label="Comments (Optional)"
            outlined
            rows="2"
            clearable
            class="mb-4"
          ></v-textarea>
          <v-switch
            v-model="finalApprovalConfirmed"
            label="I confirm all services are verified"
            color="primary"
            class="mb-4"
          ></v-switch>
          <v-btn
            color="primary"
            large
            @click="finalApprove"
            :disabled="!finalApprovalConfirmed"
            :loading="finalApprovalLoading"
          >
            Approve Operation
          </v-btn>
        </v-card-text>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import rules from '../../../utilities/validator';
import ReportDetails from '../ReportDetails';

export default {
  name: 's-receipts',
  props: {
    operation: {
      type: Object,
      default: {},
    }
  },
  components: { ReportDetails },
  data() {
    return {
      invoiceDateMenu: [],
      servicesOpted: [],
      loading: [],
      form: [],
      managers: [],
      selectedManager: '',
      submitForApprovalLoading: false,
      rules,
      filesUpload: {}, // store per service.id
      uploadLoading: false,
      removeLoading: false,
      servicesOptedSorted: [],
      finalApprovalLoading: false,
      finalApprovalComment: '',
      finalApprovalConfirmed: false,
      isDragging: false,
      selectedDocuments: [],
      consolidatePdfLoading: false,
      includeVesselReport: true,
      releasePortCallLoading: false,
      excludedServices: [
        'Agency Fees',
        'Bunkering',
        'Telecommunication',
        'Passenger Boat',
        'Supply Boat',
        'Land Transport',
        'Transport Service',
        'Pilotage'
      ],
    };
  },
  computed: {
    fileRequiredRule() {
      return (service) => {
        // Skip for excluded services
        if (this.excludedServices.includes(service.service_name)) {
          return true;
        }
        const hasNewFiles = this.filesUpload[service.id] && this.filesUpload[service.id].length > 0;
        const hasExistingFiles = service.file_urls && service.file_urls.length > 0;
        return hasNewFiles || hasExistingFiles || 'File is required';
      };
    },
    isFormDisabled() {
      return ['Billing', 'Completed', 'Cancelled'].includes(this.operation.status) && !['admin'].includes(this.$store.state.auth.profile.role);
    },
    isBillingStatus() {
      return this.operation.status === 'Billing' && ['accounts', 'admin'].includes(this.$store.state.auth.profile.role);
    },
    allDocuments() {
      const documents = [];
      this.servicesOptedSorted.forEach(service => {
        if (service.file_urls && service.file_urls.length > 0) {
          service.file_urls.forEach(file => {
            documents.push({
              id: file.file_id,
              service_name: service.service_name,
              filename: file.filename,
              url: file.url
            });
          });
        }
      });
      return documents;
    },
    hasMultipleDocuments() {
      return this.allDocuments.length > 1;
    }
  },
  methods: {
    submitForApproval() {
      if (!this.validateAllServices()) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: 'Please upload required files for all services before approving.'
        });
        return;
      }
      this.submitForApprovalLoading = true;
      this.$api.operations.submitForApproval(this.operation.id, this.selectedManager)
        .then((response) => {
          this.operation.manager_id = this.selectedManager;
          this.operation.review_requested_at = new Date();
          this.operation.status = 'Review_Requested';
          this.$store.dispatch('setAlert', { status: 'success', show: true, message: 'Operation submitted for approval successfully.' });
          this.$emit('dialogClose');
        })
        .catch((err) => {
          this.$store.dispatch('setAlert', { status: 'error', show: true, message: err.message });
        })
        .finally(() => {
          this.submitForApprovalLoading = false;
        });
    },
    handleDragOver() {
      this.isDragging = true;
    },
    handleDragLeave() {
      this.isDragging = false;
    },
    handleDrop(event, service) {
      this.isDragging = false;

      const droppedFiles = event.dataTransfer.files;
      if (!droppedFiles || droppedFiles.length === 0) return;

      // Convert FileList to Array and assign to the v-model
      const fileArray = Array.from(droppedFiles);

      // Assign to the correct service
      this.$set(this.filesUpload, service.id, fileArray);

      // Manually trigger upload
      this.uploadAsset(fileArray, service);
    },
    validateAllServices() {
      let isValid = true;

      this.servicesOptedSorted.forEach((service, index) => {
        // Skip excluded service names OR arrange_by_owner services
        if (
          this.excludedServices.includes(service.service_name) ||
          (service.metadata && service.metadata.arrange_by_owner)
        ) {
          return;
        }

        const hasNewFiles = this.filesUpload[service.id]?.length > 0;
        const hasExistingFiles = service.file_urls?.length > 0;

        if (!hasNewFiles && !hasExistingFiles) {
          let formRef = this.$refs['form' + index];
          if (formRef) {
            if (Array.isArray(formRef)) {
              formRef[0]?.validate?.();
            } else {
              formRef.validate?.();
            }
          }
          isValid = false;
        }
      });

      return isValid;
    },
    openFile(url) {
      window.open(url, '_blank');
    },
    getHintMessage(serviceName) {
      switch (serviceName) {
        case 'Port Clearance And Dues': return 'Upload Port Clearance Certificate';
        case 'Freshwater': return 'Upload Freshwater supply DO';
        case 'Garbage Disposal': return 'Upload Garbage/Oil Disposal Certificates';
        case 'Handling of Spares': return 'Upload Raks-signed DN, offlanding reports, export AWB, and email confirmation.';
        case 'Hotel Booking Crew Sign On':
        case 'Hotel Booking Crew Sign Off':
        case 'Hotel Booking Superintendent or Owners rep':
          return 'Upload crew accommodation reservation emails and crew meal details (if any).';
        case 'Transport Service': return 'Upload crew transport request email confirmation.';
        case 'Medical Consultation': return 'Upload crew medical-related emails.';
        case 'Visa Application Crew Sign On':
        case 'Visa Application Crew Sign Off':
        case 'Visa Application Superintendent or Owners rep':
          return 'Upload crew visa copy or related email requests.';
        case 'Crew Sign On Service': return 'Upload crew sign-on papers or letter of guarantee.';
        case 'Crew Sign Off Service': return 'Upload crew sign-off papers or letter of guarantee.';
        case 'Supplies': return 'Upload Master Signed DO';
        case 'SSCEC': return 'Upload SSCEC Certificates';
        case 'Porterage': return 'Upload porterage charges with principal acknowledgment, email confirmation, AWB, or item image.';
        case 'Others': return 'Upload documents for adhoc jobs, unusual charges (with principal’s email confirmation), advance payments, petty cash claims (e.g., crew meals, SIM cards), or special tariff emails.';
        case 'Cash To Master': return 'Upload Cash to Master (CTM) vouchers.';
        case 'Agency Fees': return 'Upload new customer details, including initial communication emails, billing party information, and PDA (if any).';
        case 'Bunker Surveyor': return 'Upload operations documents such as SOF, bunker survey reports, and related emails or attachments.';
        case 'Bunkering': return 'Upload statement of facts.';
        case 'Pilotage': return 'Include any cancellation or amendment emails.';
        default: return null;
      }
    },
    uploadAsset(files, service) {
      if (this.uploadLoading) return;
      if (!files || files.length === 0) {
        this.$store.dispatch('setAlert', { status: 'error', show: true, message: 'Please select an asset to upload.' });
        return;
      }

      // Validate file types
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg', 
        'image/png'
      ];

      for (let file of files) {
        if (!allowedTypes.includes(file.type)) {
          this.$store.dispatch('setAlert', { 
            status: 'error', 
            show: true, 
            message: 'Only PDF files and images (JPG, PNG, JPEG, GIF, BMP, WebP) are allowed.' 
          });
          return;
        }
      }

      this.uploadLoading = true;
      let formData = new FormData();
      for (let file of files) {
        formData.append("files[]", file);
      }
      formData.append("id", service.id);

      this.$api.operation_billed_items.upload_receipts(formData)
        .then((response) => {
          // Update immediately (no setTimeout)
          service.file_urls = response.urls;
          this.filesUpload[service.id] = []; // clear input
          this.$store.dispatch('setAlert', { message: 'Files uploaded successfully!' });
        })
        .catch(() => {
          this.$store.dispatch('setAlert', { status: 'error', show: true, message: 'Failed to upload file.' });
        })
        .finally(() => {
          this.uploadLoading = false;
        });
    },
    removeFile(service, id) {
      this.removeLoading = true;
      this.$api.operation_billed_items.remove_receipt({ operation_id: service.id, id: id })
        .then(response => {
          if (response.success) {
            service.file_urls = service.file_urls.filter(x => x.file_id != id);
            this.$store.dispatch('setAlert', { message: 'File removed successfully!' });
          } else {
            this.$store.dispatch('setAlert', { status: 'error', show: true, message: 'Failed to remove file.' });
          }
        })
        .catch(() => {
          this.$store.dispatch('setAlert', { status: 'error', show: true, message: 'Failed to remove file.' });
        })
        .finally(() => { this.removeLoading = false; });
    },
    handleOperationInit() {
      this.getBilledItems();
    },
    formSubmit(index) {
      let formRef = 'form' + index;
      formRef = this.$refs[formRef];
      if (!formRef[0].validate()) return;
      this.loading[index] = true;
      this.$api.operation_billed_items.update(this.servicesOptedSorted[index])
        .then(() => {
          this.loading[index] = false;
          this.$store.dispatch('setAlert', { status: 'success', show: true, message: 'Billed Item updated successfully' });
        })
        .catch((err) => {
          this.loading = false;
          this.$store.dispatch('setAlert', { status: 'error', show: true, message: err.message });
        });
    },
    getBilledItems() {
      this.$api.operation_billed_items.billed_items_checklist(this.operation.id)
        .then((response) => {
          this.servicesOpted = response.checklist;
          this.managers = response.managers;
          this.servicesOptedSorted = this.servicesOpted.map(service => ({
            ...service,
            amount: parseFloat(service.amount).toFixed(2)
          })).sort((a, b) => a.service_name.localeCompare(b.service_name));
          this.servicesOptedSorted.forEach(item => {
            this.invoiceDateMenu.push(false);
            this.form.push(false);
            this.loading.push(false);
          });
        })
        .catch((err) => {
          this.$store.dispatch('setAlert', { status: 'error', show: true, message: err.message });
        });
    },
    formatApprovalDate(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return null;
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    finalApprove() {
      if (!this.validateAllServices()) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: 'Please upload required files for all services before approving.'
        });
        return;
      }
      this.finalApprovalLoading = true;
      this.$api.operations.getDocsApproved(this.operation.id, this.finalApprovalComment)
        .then((response) => {
          this.finalApprovalLoading = false;
          this.operation.approved_by = response.operation?.approved_by;
          this.$store.dispatch('setAlert', { status: 'success', show: true, message: 'Operation Approved Successfully.' });
          this.$emit('dialogClose');
        })
        .catch((err) => {
          this.$store.dispatch('setAlert', { status: 'error', show: true, message: err.message });
        });
    },
    consolidateSelectedDocuments() {
      if (this.selectedDocuments.length === 0) {
        this.$store.dispatch('setAlert', {
          status: 'warning',
          show: true,
          message: 'Please select at least one document to consolidate',
        });
        return;
      }

      this.consolidatePdfLoading = true;
      
      this.$api.operation_billed_items.consolidated_report_all_services(this.operation.id, this.selectedDocuments, this.includeVesselReport)
        .then(response => {
          var b = new Blob([response.data], { type: "application/pdf" });
          const url = window.URL.createObjectURL(b);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          link.remove();

          this.$store.dispatch('setAlert', {
            status: 'success',
            show: true,
            message: 'Consolidated PDF downloaded successfully!',
          });
        })
        .catch(err => {
          if (err.response && err.response.data instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const json = JSON.parse(reader.result);
                this.$store.dispatch('setAlert', {
                  status: 'error',
                  show: true,
                  message: json.error,
                });
              } catch (parseErr) {
                console.error("Non-JSON blob:", reader.result);
              }
            };
            reader.readAsText(err.response.data);
          } else {
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: 'Failed to generate consolidated PDF',
            });
          }
        })
        .finally(() => {
          this.consolidatePdfLoading = false;
        });
    },
    releasePortCall() {
      if (!confirm('Are you sure you want to release this port call? It will be moved back to Review Requested status for the manager to make changes.')) {
        return;
      }

      this.releasePortCallLoading = true;
      this.$api.operations.releasePortCall(this.operation.id)
        .then((response) => {
          // Update operation status and clear approval fields
          this.operation.status = 'Review_Requested';
          this.operation.approval_closure = false;
          this.operation.approved_by = null;
          this.operation.approved_by_name = null;
          this.operation.approved_time = null;
          this.operation.remarks_approval = null;
          this.operation.accounts_by_id = null;
          
          this.$store.dispatch('setAlert', {
            status: 'success',
            show: true,
            message: response.message || 'Port call released successfully. Operation moved back to Review Requested status.',
          });
          
          // Optionally emit event to refresh parent component
          this.$emit('operationUpdated', this.operation);
        })
        .catch((err) => {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: err.message || 'Failed to release port call',
          });
        })
        .finally(() => {
          this.releasePortCallLoading = false;
        });
    },
  },
  watch: {
    operation() {
      this.handleOperationInit();
      this.finalApprovalConfirmed = false;
      this.finalApprovalComment = '';
      this.selectedDocuments = [];
      this.includeVesselReport = true;
    },
  },
  created() {
    this.handleOperationInit();
  },
  beforeDestroy() {
    this.finalApprovalConfirmed = false;
    this.finalApprovalComment = '';
  },
};
</script>

<style scoped>
  .custom-dropzone {
    transition: background-color 0.3s ease;
    padding: 4px;
    border-radius: 6px;
  }

  .custom-dropzone.drag-over {
    background-color: #e3f2fd;
  }

  /* Form disabling styles */
  >>> .form-disabled .v-input {
    pointer-events: none;
    opacity: 0.8;
  }

  >>> .form-disabled .v-input__control {
    pointer-events: none;
    opacity: 0.8;
  }

  >>> .form-disabled .v-input__control .v-input__slot {
    pointer-events: none;
    opacity: 0.8;
  }

  >>> .form-disabled .v-input__control .v-input__slot input {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.8;
  }

  >>> .form-disabled .v-input__control .v-input__slot .v-select__selection {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.8;
  }

  >>> .form-disabled .v-input__control .v-input__slot .v-textarea {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.8;
  }

  >>> .form-disabled .v-checkbox {
    pointer-events: none;
    opacity: 0.8;
  }

  >>> .form-disabled .v-switch {
    pointer-events: none;
    opacity: 0.8;
  }

  >>> .form-disabled .v-radio-group {
    pointer-events: none;
    opacity: 0.8;
  }

  >>> .form-disabled .v-btn {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.8;
  }

  >>> .form-disabled .v-label {
    pointer-events: none;
    opacity: 0.8;
  }

  >>> .form-disabled .v-chip-group {
    pointer-events: none;
    opacity: 0.8;
  }

  >>> .form-disabled .v-file-input {
    pointer-events: none;
    opacity: 0.8;
  }

  /* Exception: Allow Amount field to be editable when status is Billing */
  >>> .form-disabled.billing-status .amount-field {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .amount-field .v-input__control {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .amount-field .v-input__control .v-input__slot {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .amount-field .v-input__control .v-input__slot input {
    pointer-events: auto !important;
    cursor: text !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .amount-field .v-label {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  /* Exception: Allow Accounts Remarks field to be editable when status is Billing */
  >>> .form-disabled.billing-status .accounts-remarks-field {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .accounts-remarks-field .v-input__control {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .accounts-remarks-field .v-input__control .v-input__slot {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .accounts-remarks-field .v-input__control .v-input__slot input {
    pointer-events: auto !important;
    cursor: text !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .accounts-remarks-field .v-input__control .v-input__slot textarea {
    pointer-events: auto !important;
    cursor: text !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .accounts-remarks-field .v-label {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  /* Exception: Allow file upload and file chips when status is Billing */
  >>> .form-disabled.billing-status .file-upload-field {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .file-upload-field .v-file-input {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .file-upload-field .v-file-input .v-input__control {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .file-upload-field .v-file-input .v-input__control .v-input__slot {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .file-chips-field {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .file-chips-field .v-chip-group {
    pointer-events: auto !important;
    opacity: 1 !important;
  }

  >>> .form-disabled.billing-status .file-chips-field .v-chip {
    pointer-events: auto !important;
    opacity: 1 !important;
    cursor: pointer !important;
  }

  /* Exception: Allow save buttons when status is Billing */
  >>> .form-disabled.billing-status .v-btn.success--text {
    pointer-events: auto !important;
    opacity: 1 !important;
    cursor: pointer !important;
  }

  >>> .form-disabled.billing-status .v-btn.v-btn--outlined.success--text {
    pointer-events: auto !important;
    opacity: 1 !important;
    cursor: pointer !important;
  }
</style>
